import { TARGET_GROUP_LOOKUP_BY_CHILD } from '../../consts';
import { CardUrlState, StacDateBlock, UrlSelectionTypes } from '../../types';
import { formatDateForUrl } from '../../utils';

/**
 * Returns an array:
 * Strings are split by ','
 * If input is an array, input is returned unchanged.
 * Fallback returns an empty array.
 */
export const getArrayURLParams = (data: unknown) => {
    if (typeof data === 'string') return data.split(',');
    if (Array.isArray(data)) return data;
    return [];
};

/**
 * Builds the next drawer URL/selection state after an emissions drawer date change.
 * This preserves the existing drawer selection, updates the clicked target to the
 * newly selected capture/date, and clears point selections that no longer apply to that target.
 */
export const buildEmissionDrawerDateChangeState = ({
    emissionsDrawerSelection,
    day,
    targetId,
    targetGroupId,
    requestedTargetIds
}: {
    emissionsDrawerSelection?: CardUrlState | null;
    day: StacDateBlock;
    targetId: string;
    targetGroupId: string;
    requestedTargetIds: string[];
}): CardUrlState => {
    /**
     * If the of `day`'s targetId (the new selection) belongs to the same group as an existing selection
     * but is not the same actual `targetId`, replace the old targetId with the new targetId
     */
    const newTargetIds = requestedTargetIds.map((id) => {
        if (targetId !== id && targetGroupId && TARGET_GROUP_LOOKUP_BY_CHILD[id] === targetGroupId) {
            return targetId;
        }
        return id;
    });

    const nextDrawerSelection = structuredClone(emissionsDrawerSelection) || ({} as CardUrlState);
    nextDrawerSelection['selection-type'] = UrlSelectionTypes.Details;
    nextDrawerSelection.targets = newTargetIds;

    if (
        typeof emissionsDrawerSelection?.['selected-lng'] === 'number' &&
        typeof emissionsDrawerSelection?.['selected-lat'] === 'number'
    ) {
        nextDrawerSelection['selected-lng'] = emissionsDrawerSelection['selected-lng'];
        nextDrawerSelection['selected-lat'] = emissionsDrawerSelection['selected-lat'];
    }

    const nextDate = formatDateForUrl(day.date);

    // Reset plume point selections for the clicked target; they do not carry across captures.
    newTargetIds.forEach((id) => {
        if (id === targetId) {
            delete nextDrawerSelection[`target-${id}-points`];
        }
    });

    nextDrawerSelection[`target-${targetId}-date`] = nextDate;
    nextDrawerSelection[`target-${targetId}-itemId`] = day.id;
    nextDrawerSelection[`target-${targetId}-collection`] = day.collection;

    return nextDrawerSelection;
};
